import React, { useReducer, useState } from "react";
import { Lookup } from "../models/lookup.model";
import { lookupReducer } from "../reducers/lookup.reducer";
import { questionnaireReducer } from "../reducers/questionnaire.reducer";
import {
  QuestionnaireGlobalModel,
  Section,
} from "../models/questionnaire.model";
import { Domain } from "../models/domain.model";
import { domainReducer } from "../reducers/domain.reducer";
import { toast, ToastPosition } from "react-toastify";
import { useHotkeys } from "react-hotkeys-hook";
import HOT_KEYS from "misc/hot-keys";

export const AppContext = React.createContext({
  lookup: {} as Lookup,
  domain: {} as Domain,
  questionnaire: {} as QuestionnaireGlobalModel,
  isLoading: false,
  helpMode: false,
  setHelpMode: (value: boolean) => {},
  setIsLoading: (value: boolean) => {},
  setLookup: (lookup: Lookup) => {},
  setDomain: (domain: Domain) => {},
  notifyError: (value: string) => {},
  notifySuccess: (value: string) => {},
  updateQuestionnaire: (action: string, value: any) => {},
  previewPHQ: (phqVersionID: number) => {},
});

const initialDomain = {} as Domain;
const initialLookup = {} as Lookup;
const initialQuestionnaire = {} as QuestionnaireGlobalModel;
initialQuestionnaire.Sections = [{} as Section];

const AppContextProvider = (props: { children: React.ReactNode }) => {
  const [lookup, dispatch] = useReducer(lookupReducer, initialLookup);
  const [domain, dispatchDomain] = useReducer(domainReducer, initialDomain);
  const [isLoading, setLoading] = useState(false);
  const [helpMode, setHelpMode] = useState(false);
  const [questionnaire, questionnaireDispatch] = useReducer(
    questionnaireReducer,
    initialQuestionnaire
  );

  const lookupHandler = (value: Lookup) => {
    dispatch({ type: "set", value });
  };

  const questionnaireHandler = (action: string, value: any) => {
    questionnaireDispatch({ type: action, value });
  };

  const domainHandler = (value: Domain) => {
    dispatchDomain({ type: "set", value });
  };

  const previewPHQ = (phqId: number) => {
    const baseUrl = process.env.REACT_APP_PREVIEW_URL;
    let url = questionnaire.ViewMode
      ? `${baseUrl}?preview=true&phqversionid=${questionnaire.VersionIDViewing}&previewVersion=${true}`
      : `${baseUrl}?preview=true&phqid=${phqId}&previewVersion=${false}`;

    window.open(url, "_blank");
  };

  const toastIngirdients = {
    position: "top-right" as ToastPosition,
    hideProgressBar: true,
    closeOnClick: true,
    pauseOnHover: true,
    draggable: true,
    progress: undefined
  };

  const notifyError = (message: string) => {
    toast.error(message, toastIngirdients);
  };

  const notifySuccess = (message: string) => {
    toast.success(message, toastIngirdients);
  };

  useHotkeys(HOT_KEYS.HelpMode, () => {
    setHelpMode(h => !h);
  });

  return (
    <AppContext.Provider
      value={{
        lookup: lookup,
        domain: domain,
        questionnaire: questionnaire,
        isLoading: isLoading,
        helpMode: helpMode,
        setHelpMode: setHelpMode,
        setIsLoading: setLoading,
        setLookup: lookupHandler,
        previewPHQ: previewPHQ,
        setDomain: domainHandler,
        updateQuestionnaire: questionnaireHandler,
        notifyError: notifyError,
        notifySuccess: notifySuccess,
      }}
    >
      {props.children}
    </AppContext.Provider>
  );
};

export default AppContextProvider;
